import React, { useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';
import { useRoutes } from "react-router-dom";
import { Provider } from "react-redux";
import { HelmetProvider } from "react-helmet-async";
import { store } from "./redux/store.js";
import routes from "./routes.js";
import { subscribeToWaitingServiceWorker, syncWaitingServiceWorker, updateApp } from './serviceWorkerRegistration.js';

import { SidebarProvider } from "./contexts/SidebarContext.js";
import { LayoutProvider } from "./contexts/LayoutContext.js";
import { AuthProvider } from "./contexts/loginContext.js";

const hasDebugParam = (paramName) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const search = window.location.search || '';
  const directParams = new URLSearchParams(search);
  if (directParams.get(paramName) === '1') {
    return true;
  }

  const normalizedSearch = search.replace(/\?/g, (char, index) => (index === 0 ? char : '&'));
  const normalizedParams = new URLSearchParams(normalizedSearch);
  return normalizedParams.get(paramName) === '1';
};

const CONSENT_COOKIE_NAME = 'flex-CookieConsent';
const BANNER_HIDE_DAYS = 15;
const BANNER_HIDE_MS = BANNER_HIDE_DAYS * 24 * 60 * 60 * 1000;
const INSTALL_BANNER_HIDE_UNTIL_KEY = 'flex-install-banner-hide-until';
const UPDATE_BANNER_HIDE_UNTIL_KEY = 'flex-update-banner-hide-until';

const isPWAMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true;
};

const isIOSDevice = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = window.navigator.userAgent || '';
  const iOSUserAgent = /iPad|iPhone|iPod/i.test(userAgent);
  const iPadOSDesktopUA = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return iOSUserAgent || iPadOSDesktopUA;
};

const getCookieValue = (name) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const nameEq = `${name}=`;
  const cookies = document.cookie ? document.cookie.split(';') : [];

  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(nameEq)) {
      return decodeURIComponent(cookie.substring(nameEq.length));
    }
  }

  return null;
};

const setCookieValue = (name, value, days = 999) => {
  if (typeof document === 'undefined') {
    return;
  }

  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + days);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expiresAt.toUTCString()}; path=/; SameSite=Lax`;
};

const getBannerHideUntil = (storageKey) => {
  if (typeof window === 'undefined') {
    return 0;
  }

  const value = window.localStorage.getItem(storageKey);
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) {
    return 0;
  }

  return timestamp;
};

const setBannerHideUntil = (storageKey, hideMs = BANNER_HIDE_MS) => {
  if (typeof window === 'undefined') {
    return;
  }

  const hideUntil = Date.now() + hideMs;
  window.localStorage.setItem(storageKey, String(hideUntil));
};

const isBannerHiddenByCooldown = (storageKey) => getBannerHideUntil(storageKey) > Date.now();

const FooterBannerQueue = ({ canRenderBanners = false }) => {
  const isTestUpdateMode = hasDebugParam('testUpdate');
  const isTestInstallMode = hasDebugParam('testInstall');
  const BANNER_DISPLAY_DELAY_MS = 2000;

  const [hasPendingUpdate, setHasPendingUpdate] = useState(false);
  const [dismissedUpdate, setDismissedUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdateHiddenByCooldown, setIsUpdateHiddenByCooldown] = useState(() => isBannerHiddenByCooldown(UPDATE_BANNER_HIDE_UNTIL_KEY));

  const [promptEvent, setPromptEvent] = useState(null);
  const [hasInstallPrompt, setHasInstallPrompt] = useState(false);
  const [dismissedInstall, setDismissedInstall] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isIosInstallHint, setIsIosInstallHint] = useState(false);
  const [isInstallHiddenByCooldown, setIsInstallHiddenByCooldown] = useState(() => isBannerHiddenByCooldown(INSTALL_BANNER_HIDE_UNTIL_KEY));
  const [isInstallingInBackground, setIsInstallingInBackground] = useState(false);
  const [isNewlyInstalled, setIsNewlyInstalled] = useState(false);

  const [hasCookieConsent, setHasCookieConsent] = useState(() => Boolean(getCookieValue(CONSENT_COOKIE_NAME)));
  const [isBannerDelayElapsed, setIsBannerDelayElapsed] = useState(false);

  useEffect(() => {
    syncWaitingServiceWorker().catch(() => {});
    const unsubscribe = subscribeToWaitingServiceWorker((reg) => {
      if (isBannerHiddenByCooldown(UPDATE_BANNER_HIDE_UNTIL_KEY)) {
        setIsUpdateHiddenByCooldown(true);
        return;
      }

      setIsUpdateHiddenByCooldown(false);
      setHasPendingUpdate(Boolean(reg));
    });

    let updateTimer;
    if (isTestUpdateMode) {
      updateTimer = window.setTimeout(() => {
        if (!isBannerHiddenByCooldown(UPDATE_BANNER_HIDE_UNTIL_KEY)) {
          setHasPendingUpdate(true);
          setIsUpdateHiddenByCooldown(false);
        }
      }, 500);
    }

    return () => {
      if (updateTimer) {
        window.clearTimeout(updateTimer);
      }
      unsubscribe();
    };
  }, [isTestUpdateMode]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || isTestUpdateMode) {
      return undefined;
    }

    let registrationRef = null;
    let installingCleanup = null;
    let updateFoundHandler = null;

    const watchInstallingWorker = (worker) => {
      if (!worker) {
        return;
      }

      const handleStateChange = () => {
        if (worker.state === 'installed' && navigator.serviceWorker.controller) {
          if (isBannerHiddenByCooldown(UPDATE_BANNER_HIDE_UNTIL_KEY)) {
            setIsUpdateHiddenByCooldown(true);
            return;
          }

          setIsUpdateHiddenByCooldown(false);
          setHasPendingUpdate(true);
        }
      };

      worker.addEventListener('statechange', handleStateChange);

      if (installingCleanup) {
        installingCleanup();
      }

      installingCleanup = () => {
        worker.removeEventListener('statechange', handleStateChange);
      };
    };

    const bindUpdateFound = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) {
          return;
        }

        registrationRef = registration;
        updateFoundHandler = () => {
          watchInstallingWorker(registration.installing);
        };

        registration.addEventListener('updatefound', updateFoundHandler);
        watchInstallingWorker(registration.installing);
      } catch (error) {
        console.error('Failed to bind update listener:', error);
      }
    };

    bindUpdateFound();

    return () => {
      if (installingCleanup) {
        installingCleanup();
      }

      if (registrationRef && updateFoundHandler) {
        registrationRef.removeEventListener('updatefound', updateFoundHandler);
      }
    };
  }, [isTestUpdateMode]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleBeforeInstallPrompt = (event) => {
      if (isPWAMode()) {
        return;
      }

      if (isBannerHiddenByCooldown(INSTALL_BANNER_HIDE_UNTIL_KEY)) {
        setIsInstallHiddenByCooldown(true);
        return;
      }

      event.preventDefault();
      setIsInstallHiddenByCooldown(false);
      setPromptEvent(event);
      setIsIosInstallHint(false);
      setHasInstallPrompt(true);
    };

    const handleAppInstalled = () => {
      setPromptEvent(null);
      setIsIosInstallHint(false);
      
      setIsInstallingInBackground(true);
      setTimeout(() => {
        setIsInstallingInBackground(false);
        setIsNewlyInstalled(true);
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    const iosEligible = isIOSDevice() && !isPWAMode();
    const installInCooldown = isBannerHiddenByCooldown(INSTALL_BANNER_HIDE_UNTIL_KEY);
    setIsInstallHiddenByCooldown(installInCooldown);
    setIsIosInstallHint(!installInCooldown && iosEligible);

    if (!installInCooldown && iosEligible) {
      setHasInstallPrompt(true);
    }

    let installTimer;
    if (isTestInstallMode && !isPWAMode()) {
      installTimer = window.setTimeout(() => {
        if (isBannerHiddenByCooldown(INSTALL_BANNER_HIDE_UNTIL_KEY)) {
          setIsInstallHiddenByCooldown(true);
          return;
        }

        setIsInstallHiddenByCooldown(false);
        setPromptEvent(null);
        setIsIosInstallHint(false);
        setHasInstallPrompt(true);
      }, 500);
    }

    return () => {
      if (installTimer) {
        window.clearTimeout(installTimer);
      }
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isTestInstallMode]);

  useEffect(() => {
    if (!canRenderBanners) {
      setIsBannerDelayElapsed(false);
      return undefined;
    }

    const delayTimer = window.setTimeout(() => {
      setIsBannerDelayElapsed(true);
    }, BANNER_DISPLAY_DELAY_MS);

    return () => {
      window.clearTimeout(delayTimer);
    };
  }, [BANNER_DISPLAY_DELAY_MS, canRenderBanners]);

  const handleUpdate = async () => {
    setIsUpdating(true);

    if (isTestUpdateMode) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setIsUpdating(false);
      setDismissedUpdate(true);
      setHasPendingUpdate(false);
      return;
    }

    try {
      const result = await updateApp();
      if (result && ['activating', 'up-to-date', 'unsupported', 'no-registration'].includes(result.status)) {
        setDismissedUpdate(true);
        setHasPendingUpdate(false);
      }
    } catch (error) {
      console.error('Failed to update app:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleInstall = async () => {
    setIsInstalling(true);

    if (promptEvent) {
      try {
        promptEvent.prompt();
        const choiceResult = await promptEvent.userChoice;
        if (choiceResult && choiceResult.outcome === 'accepted') {
          // Native prompt accepted! Do not dismiss.
        } else {
          setDismissedInstall(true);
          setPromptEvent(null);
          setHasInstallPrompt(false);
        }
      } catch (error) {
        console.error('PWA install prompt failed:', error);
        setPromptEvent(null);
        setHasInstallPrompt(false);
      } finally {
        setIsInstalling(false);
      }
      return;
    }

    if (isIosInstallHint) {
      setIsInstalling(false);
      setHasInstallPrompt(false);
      setDismissedInstall(true);
      return;
    }

    setIsInstalling(false);
  };

  const handleOpenApp = () => {
    window.location.href = window.location.origin + window.location.pathname + '?open=1';
  };

  const handleAcceptCookies = () => {
    setCookieValue(CONSENT_COOKIE_NAME, 'true', 999);
    setHasCookieConsent(true);
  };

  const dismissInstallBannerForDays = () => {
    setBannerHideUntil(INSTALL_BANNER_HIDE_UNTIL_KEY);
    setIsInstallHiddenByCooldown(true);
    setPromptEvent(null);
    setHasInstallPrompt(false);
    setDismissedInstall(true);
    setIsNewlyInstalled(false);
  };

  const dismissUpdateBannerForDays = () => {
    setBannerHideUntil(UPDATE_BANNER_HIDE_UNTIL_KEY);
    setIsUpdateHiddenByCooldown(true);
    setHasPendingUpdate(false);
    setDismissedUpdate(true);
  };

  let activeBanner = null;
  if (canRenderBanners && isBannerDelayElapsed) {
    if (!hasCookieConsent) {
      activeBanner = 'cookie';
    } else if ((hasInstallPrompt || isNewlyInstalled || isInstallingInBackground) && !dismissedInstall && !isInstallHiddenByCooldown) {
      activeBanner = 'install';
    } else if (hasPendingUpdate && !dismissedUpdate && !isUpdateHiddenByCooldown) {
      activeBanner = 'update';
    }
  }

  if (!activeBanner || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div className="app-footer-banner-stack">
      {activeBanner === 'update' && (
        <div className="app-footer-banner app-footer-banner--update">
          <div className="app-footer-banner__content">
            <span className="app-footer-banner__icon" aria-hidden="true">
              <FontAwesomeIcon icon={faWarning} />
            </span>
            <span className="app-footer-banner__message">Uma nova versão do app está disponível.</span>
            <button
              className="app-footer-banner__btn"
              onClick={handleUpdate}
              disabled={isUpdating}
            >
              {isUpdating ? 'Atualizando...' : 'Atualizar agora'}
            </button>
          </div>
          <button
            className="app-footer-banner__close"
            onClick={dismissUpdateBannerForDays}
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}
      {activeBanner === 'install' && (
        <div className="app-footer-banner app-footer-banner--install">
          <div className="app-footer-banner__content">
            <span className="app-footer-banner__icon" aria-hidden="true">
              <FontAwesomeIcon icon={faWarning} />
            </span>
            <span className="app-footer-banner__message">
              {isNewlyInstalled 
                ? 'Instalação concluída! Acesse pela tela de início ou clique abaixo.'
                : isInstallingInBackground
                  ? 'Aguarde, finalizando a instalação do aplicativo...'
                  : promptEvent
                    ? 'Instale o app e aproveite todos os recursos.'
                    : 'No iPhone ou iPad, toque em Compartilhar e depois em Adicionar à Tela de Início.'}
            </span>
            <button
              className="app-footer-banner__btn"
              onClick={isNewlyInstalled ? handleOpenApp : handleInstall}
              disabled={isInstalling || isInstallingInBackground}
            >
              {isNewlyInstalled 
                ? 'Abrir aplicativo'
                : isInstallingInBackground
                  ? 'Instalando...'
                  : isInstalling 
                    ? 'Abrindo instalação...' 
                    : promptEvent ? 'Instalar app' : 'Entendi'}
            </button>
          </div>
          <button
            className="app-footer-banner__close"
            onClick={dismissInstallBannerForDays}
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}
      {activeBanner === 'cookie' && (
        <div className="app-footer-banner app-footer-banner--cookie">
          <div className="app-footer-banner__content">
            <span className="app-footer-banner__icon" aria-hidden="true">
              <FontAwesomeIcon icon={faWarning} />
            </span>
            <div className="app-footer-banner__copy">
              <p className="mb-0">Este site utiliza cookies para melhorar a experiência do usuário.</p>
            </div>
            <button
              className="app-footer-banner__btn"
              onClick={handleAcceptCookies}
            >
              Eu entendo
            </button>
          </div>
          <button
            className="app-footer-banner__close"
            onClick={handleAcceptCookies}
            aria-label="Fechar aviso"
          >
            ×
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

const App = () => {
  const content = useRoutes(routes);
  const [isSecondLoadingScreenCompleted, setIsSecondLoadingScreenCompleted] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (window.__R1_LOGIN_LOADING_READY__ === true) {
      setIsSecondLoadingScreenCompleted(true);
    }

    const handleLoginLoadingReady = () => {
      setIsSecondLoadingScreenCompleted(true);
    };

    window.addEventListener('r1eventech:login-loader-ready', handleLoginLoadingReady);

    return () => {
      window.removeEventListener('r1eventech:login-loader-ready', handleLoginLoadingReady);
    };
  }, []);

  return (
    <HelmetProvider>
      <Provider store={store}>
        <SidebarProvider>
          <LayoutProvider>
            <AuthProvider>
              <FooterBannerQueue canRenderBanners={isSecondLoadingScreenCompleted} />
              {content}
            </AuthProvider>
          </LayoutProvider>
        </SidebarProvider>
      </Provider>
    </HelmetProvider>
  );
};

export default App;
